import React, { useState } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import Table from 'components/Table';
import {
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import moment from 'moment';
import Button from 'components/Button';
import CheckIcon from 'assets/icon-components/checkIcon';
import { useNavigate, useParams } from 'react-router-dom';
import { DASHBOARD_ROUTES } from 'Routes';
import { selectCompany } from 'store/companySlide';
import { CompanyBranch, CompanyInfo } from 'data/models/company.model';
import { useSelector } from 'react-redux';
import { getBranchCompanies } from 'data/api/company.api';
import { IListRequest } from '@data/models/common.model';
import { useAppDispatch } from 'store/hooks';
import { saveNavigateLinkHeader } from 'store/navigateSlide';

const columns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Branch Name',
    flex: 1,
    sortable: true,
    minWidth: 300,
    renderHeader: () => {
      return (
        <Typography
          fontWeight={700}
          lineHeight={'16px'}
          fontSize={'13px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            marginLeft: '20px',
          }}
        >
          Branch Name
        </Typography>
      );
    },
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Typography
          fontWeight={500}
          lineHeight={'16px'}
          fontSize={'12px'}
          sx={{
            textWrap: 'wrap',
            textAlign: 'start',
            marginLeft: '20px',
          }}
        >
          {params.value}
        </Typography>
      );
    },
  },
  {
    field: 'enrollMember',
    headerName: 'Active Users',
    flex: 1,
    sortable: true,
    minWidth: 100,
    renderHeader: () => {
      return (
        <Typography
          fontWeight={700}
          lineHeight={'16px'}
          fontSize={'13px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          Active Users
        </Typography>
      );
    },
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Typography
          fontWeight={500}
          lineHeight={'16px'}
          fontSize={'12px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {params.value}
        </Typography>
      );
    },
  },
  {
    field: 'triages',
    headerName: 'Triage Notifications',
    flex: 1,
    minWidth: 150,
    sortable: true,
    renderHeader: () => {
      return (
        <Typography
          fontWeight={700}
          lineHeight={'16px'}
          fontSize={'13px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          Triage Notifications
        </Typography>
      );
    },
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Typography
          fontWeight={500}
          lineHeight={'16px'}
          fontSize={'12px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {params.value}
        </Typography>
      );
    },
  },
  {
    field: 'possibleFeedback',
    headerName: 'Positive Feedback',
    flex: 1,
    sortable: true,
    minWidth: 150,
    renderHeader: () => {
      return (
        <Typography
          fontWeight={700}
          lineHeight={'16px'}
          fontSize={'13px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          Positive Feedback
        </Typography>
      );
    },
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Typography
          fontWeight={500}
          lineHeight={'16px'}
          fontSize={'12px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {params.value}
        </Typography>
      );
    },
  },
  {
    field: 'optOuts',
    headerName: 'Opt-Outs',
    flex: 1,
    minWidth: 150,
    sortable: true,
    renderHeader: () => {
      return (
        <Typography
          fontWeight={700}
          lineHeight={'16px'}
          fontSize={'13px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          Opt-Outs
        </Typography>
      );
    },
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Typography
          fontWeight={500}
          lineHeight={'16px'}
          fontSize={'12px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {params.value}
        </Typography>
      );
    },
  },
  {
    field: 'uniqueMemberEngagement',
    headerName: 'Unique Member Engagement',
    flex: 1,
    sortable: true,
    minWidth: 150,
    renderHeader: () => {
      return (
        <Typography
          fontWeight={700}
          lineHeight={'16px'}
          fontSize={'13px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          Unique Member Engagement
        </Typography>
      );
    },
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Typography
          fontWeight={500}
          lineHeight={'16px'}
          fontSize={'12px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {Number(params.value).toFixed(1) + '%'}
        </Typography>
      );
    },
  },
  {
    field: 'messageResponseRate',
    headerName: 'Member Response Rate',
    flex: 1,
    sortable: true,
    minWidth: 150,
    renderHeader: () => {
      return (
        <Typography
          fontWeight={700}
          lineHeight={'16px'}
          fontSize={'13px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          Member Response Rate
        </Typography>
      );
    },
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Typography
          fontWeight={500}
          lineHeight={'16px'}
          fontSize={'12px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {Number(params.value).toFixed(1) + '%'}
        </Typography>
      );
    },
  },
  {
    field: 'createdDate',
    headerName: 'Date Added',
    flex: 1,
    minWidth: 150,
    sortable: true,
    renderHeader: () => {
      return (
        <Typography
          fontWeight={700}
          lineHeight={'16px'}
          fontSize={'13px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          Date Added
        </Typography>
      );
    },
    renderCell: (params: GridRenderCellParams) => {
      return (
        <Typography
          fontWeight={500}
          lineHeight={'16px'}
          fontSize={'12px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            color: '#000000',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {moment(params.value).format('MM/DD/YYYY h:mm A')}
        </Typography>
      );
    },
  },
  {
    field: 'isActive',
    headerName: 'Active',
    flex: 1,
    sortable: true,
    minWidth: 150,
    renderHeader: () => {
      return (
        <Typography
          fontWeight={700}
          lineHeight={'16px'}
          fontSize={'13px'}
          sx={{
            display: '-webkit-box',
            lineClamp: 2,
            overflow: 'hidden',
            whiteSpace: 'pre-wrap',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          Active
        </Typography>
      );
    },
    renderCell: (params: GridRenderCellParams) => {
      return <>{params.value && <CheckIcon />}</>;
    },
  },
];

export const BranchCompanyTable = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [data, setData] = React.useState<CompanyBranch[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = React.useState<boolean>(true);
  const { company_id } = useParams();

  const [filter, setFilter] = useState<IListRequest>({
    sortBy: 'createdDate',
    order: 'desc',
    page: 0,
    pageSize: 50,
  });
  const companyInfo: CompanyInfo = useSelector(selectCompany);
  const fetchCompanyBranch = async () => {
    try {
      if (company_id) {
        const res = await getBranchCompanies(company_id, filter);
        setData(res.companies);
        setRowCount(res?.pager?.total || 0);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const handleAddBranchCompany = () => {
    navigate(
      DASHBOARD_ROUTES.ADD_NEW_COMPANY_PARENT(companyInfo.companyId || ''),
    );
  };

  const handleNavigateToDetail = (fromLink: string, toLink: string) => {
    dispatch(saveNavigateLinkHeader({ fromLink, toLink }));
    navigate(toLink);
  };

  const handleRowClick: GridEventListener<'rowClick'> = (params) => {
    handleNavigateToDetail(
      DASHBOARD_ROUTES.COMPANY_LIST,
      DASHBOARD_ROUTES.COMPANY_DETAIL + params.id,
    );
  };

  React.useEffect(() => {
    setLoading(true);
    fetchCompanyBranch();
  }, [filter, company_id]);

  return (
    <>
      <Stack gap={1} width={'100%'} marginBottom={'48px'}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Stack gap={2}>
            <Typography fontWeight={700} fontSize={'18px'}>
              Branch Locations
            </Typography>
            <Typography fontWeight={500} fontSize={'15px'}>
              {rowCount} records
            </Typography>
          </Stack>
          <Button
            variant="outlined"
            sx={{
              fontWeight: 700,
              fontSize: '14px',
              border: '1px solid rgba(33, 59, 84, 1)',
            }}
            onClick={handleAddBranchCompany}
          >
            Add Branch Company
          </Button>
        </Stack>
        {loading ? (
          <Stack
            width={'100%'}
            height={'500px'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <CircularProgress color="inherit" />
          </Stack>
        ) : (
          <Table
            getRowId={(r) => r.companyId}
            loading={status === 'loading'}
            rows={data || []}
            columns={columns}
            paginationMode="server"
            sortingMode="server"
            rowCount={rowCount}
            disableColumnFilter={true}
            disableColumnMenu={true}
            filter={filter}
            onFilterChange={setFilter}
            pageSizeOptions={[10, 25, 50, 100]}
            onRowClick={handleRowClick}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#F8F9FB',
                borderTop: '1px solid #DBD8D8',
                borderBottom: '1px solid #DBD8D8',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: '#213B54',
                fontWeight: 700,
                fontSize: '13px',
                lineHeight: '20px',
                letterSpacing: '0.1px',
              },
              '& .MuiDataGrid-cell': {
                color: '#000000',
                fontWeight: 500,
                fontSize: '12px !important',
                cursor: 'pointer',
              },
            }}
          />
        )}
      </Stack>
    </>
  );
};
